/**
 * URL helpers for the SEO surface. Kept free of "server-only" so the
 * client price-check widget can build the same hrefs.
 *
 * Listing pages live at /elan/<source>-<external id> ("bina-6464244"):
 * readable, no colon to escape, and the source prefix keeps two sites'
 * numeric ids from ever colliding.
 */

const SOURCE_TO_SLUG: Record<string, string> = {
  bina_az: "bina",
  emlak_az: "emlak",
  ev10_az: "ev10",
  arenda_az: "arenda",
  // Without these the new sites had no /elan page, and the result export's
  // links to them 404'd (2026-10-17).
  tap_az: "tap",
  lalafo_az: "lalafo",
  yeniemlak_az: "yeniemlak",
  emlaktap_az: "emlaktap",
};
const SLUG_TO_SOURCE: Record<string, string> = Object.fromEntries(
  Object.entries(SOURCE_TO_SLUG).map(([k, v]) => [v, k]),
);

export function listingSlug(id: string): string | null {
  const [source, ext] = id.split(":");
  const s = SOURCE_TO_SLUG[source];
  if (!s || !ext) return null;
  return `${s}-${ext}`;
}

/** Whether a slug can be a listing page: a known source and a numeric
 *  id. The middleware answers anything else with a cheap 404, so a
 *  made-up id never renders, calls the API or lands in the page cache.
 *  Built from the source map so a new site's pages are reachable the day
 *  its links appear (tap, lalafo, yeniemlak and emlaktap 404'd from
 *  2026-10-17 to 2026-10-20 behind a hand-kept list). */
const LISTING_SLUG_RE = new RegExp(`^(?:${Object.values(SOURCE_TO_SLUG).join("|")})-\\d{1,12}$`);

export function isListingSlug(slug: string): boolean {
  return LISTING_SLUG_RE.test(slug);
}

export function listingIdFromSlug(slug: string): string | null {
  const m = /^([a-z0-9]+)-([0-9A-Za-z_-]{1,40})$/.exec(slug);
  if (!m) return null;
  const source = SLUG_TO_SOURCE[m[1]];
  return source ? `${source}:${m[2]}` : null;
}

export function listingPath(id: string): string | null {
  const s = listingSlug(id);
  return s ? `/elan/${s}` : null;
}

export function sourceHost(source: string): string {
  return {
    bina_az: "bina.az", emlak_az: "emlak.az", ev10_az: "ev10.az", arenda_az: "arenda.az",
    tap_az: "tap.az", lalafo_az: "lalafo.az", yeniemlak_az: "yeniemlak.az",
    emlaktap_az: "emlaktap.az",
  }[source] ?? source;
}

/**
 * A landing page's filters as /listings URL params: the same search,
 * with every filter control available. Matches the landing page's own
 * count exactly (the page's category, a rayon expanded server-side, the 5+
 * bucket as rooms=5, which the listings client widens to 5 and up).
 */
export function landingListingsQuery(
  location: { id: string } | null,
  filters: Record<string, unknown>,
): URLSearchParams {
  const q = new URLSearchParams();
  q.set("leased", filters.leased ? "true" : "false");
  // /menziller filters to apartments; /evler sends no category (all).
  const category = filters.category as string | undefined;
  if (category) q.set("category", category);
  if (location) {
    // A landmark is a location id too: /listings filters it to the same
    // radius around its pin the page counts, and names it in the picker.
    q.append("location_ids", location.id);
  }
  const bt = filters.building_type as string | undefined;
  if (bt) q.set("building_type", bt);
  const rooms = filters.room_counts as number[] | undefined;
  if (rooms?.length) q.append("rooms", String(Math.min(rooms[0], 5)));
  (filters.feature_tags as string[] | undefined)?.forEach((tg) => q.append("tags", tg));
  for (const k of ["seller_type", "has_bill_of_sale", "has_mortgage", "has_repair", "rent_period", "metro_walk"]) {
    if (filters[k]) q.set(k, String(filters[k]));
  }
  (filters.deal_tiers as string[] | undefined)?.forEach((tr) => q.append("deal_tier", tr));
  return q;
}

/** A page a search engine or an assistant lands people on: a listing,
 *  a residence, a district or residence landing. These pages carry
 *  their own guidance and get no corner prompts (release card, trial
 *  sheet): the 2026-10-14 review found those ignored, and the visitor
 *  came for the page, not for us. Locale prefix optional. */
export function isSeoEntryPath(path: string): boolean {
  const p = path.replace(/^\/(az|ru|en)(?=\/|$)/, "") || "/";
  return /^\/(elan|residence|menziller|evler)\/[^/]+/.test(p);
}
